import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Star, AlertTriangle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import ProductVisual from "@/components/ProductVisual";
import NotifyForm from "@/components/NotifyForm";
import { getProductPhoto, getVialPhoto, getAccessoryPhoto, isHeroVial } from "@/lib/productPhotos";
import { splitProductName } from "@/lib/productName";
import { isProductFullyOOS, getEffectiveStock, stockLabel } from "@/lib/stock";
import { getCardShortDescription } from "@/lib/productCardCopy";

const TAB_ORDER = ["SEALED", "OPEN", "PEN"];
const CYCLE_MS = 900;

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const { format: fmtPrice } = useCurrency();
  const { main, sub } = splitProductName(product.name);
  const oos = isProductFullyOOS(product);
  const variantList = Array.isArray(product.variants) ? product.variants : [];
  const isTrueMultiVariant = variantList.length > 1;
  // Card-level low-stock: surface an exact count when there's a single SKU
  // (either no variants at all, or exactly one variant that acts as the
  // product's sole SKU). Multi-variant products fall back to the generic
  // "IN STOCK" label because the number depends on which variant a buyer picks.
  const singleSkuStock = isTrueMultiVariant
    ? null
    : variantList.length === 1
      ? getEffectiveStock(product, variantList[0].pack_size)
      : getEffectiveStock(product);
  const cardStockChip = singleSkuStock !== null && singleSkuStock > 0
    ? stockLabel(singleSkuStock)
    : null;

  // Build the list of card photos.
  //   Pen kits:   cycle through SEALED / OPEN / PEN if we have them.
  //   Raw vials & Vial kits: show a single static vial.webp (no cycling, one image only).
  //   Accessories: show a single hero.webp (no cycling, one image only).
  // If no photo exists for the product yet we fall back to the animated
  // ProductVisual placeholder so the card still looks intentional.
  const photos = product.format === "raw" || product.format === "vial_kit"
    ? [getVialPhoto(product.slug)].filter(Boolean)
    : product.format === "accessory"
      ? [getAccessoryPhoto(product.slug)].filter(Boolean)
      : TAB_ORDER.map((t) => getProductPhoto(product.slug, t)).filter(Boolean);
  const sealedPhoto = photos[0] || null;
  const canCycle = photos.length > 1;

  // Cycling state
  const [idx, setIdx] = useState(0);
  const [cycling, setCycling] = useState(false);
  const timerRef = useRef(null);
  const rootRef = useRef(null);

  // Hover-capable device (desktop mouse) vs touch device
  const hoverCapable = useRef(false);
  useEffect(() => {
    hoverCapable.current = typeof window !== "undefined" && window.matchMedia("(hover: hover)").matches;
  }, []);

  const startCycle = () => {
    if (!canCycle) return;
    setCycling(true);
  };
  const stopCycle = () => {
    setCycling(false);
    setIdx(0);
  };

  // Drive the cycle when active
  useEffect(() => {
    if (!cycling) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % photos.length);
    }, CYCLE_MS);
    return () => clearInterval(timerRef.current);
  }, [cycling, photos.length]);

  // Touch: stop cycling when the user taps anywhere outside this card
  useEffect(() => {
    if (!cycling || hoverCapable.current) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) stopCycle();
    };
    document.addEventListener("touchstart", onDocClick, { passive: true });
    document.addEventListener("mousedown", onDocClick);
    return () => {
      document.removeEventListener("touchstart", onDocClick);
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [cycling]);

  return (
    <div
      ref={rootRef}
      className="group flex flex-col overflow-hidden rounded-xl panel-warm bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(30,58,138,0.18)]"
      data-testid={`product-card-${product.slug}`}
    >
      <Link
        to={`/product/${product.slug}`}
        className={`relative block aspect-[5/4] overflow-hidden bg-white ${product.format === "raw" && isHeroVial(product.slug) ? "vial-hero" : ""}`}
        onMouseEnter={() => hoverCapable.current && startCycle()}
        onMouseLeave={() => hoverCapable.current && stopCycle()}
        data-testid={`product-card-image-${product.slug}`}
      >
        {sealedPhoto ? (
          // Stack every available photo, crossfade by toggling opacity via `idx`
          photos.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={product.name}
              className={`absolute inset-0 h-full w-full object-contain object-center transition-opacity duration-[150ms] ease-linear ${
                i === idx ? "opacity-100" : "opacity-0"
              }`}
              // Every card image is below the LCP element on the home
              // and catalog pages. Lazy loading here removes ~1 MB of
              // eager image transfer from the /peptides initial paint
              // and roughly 400 KB from PDPs that render related-product
              // cards. Only the first frame is fetched immediately by
              // the browser's lazy-load heuristics when it's in the
              // viewport; subsequent crossfade frames wait until near-
              // viewport intersection.
              loading="lazy"
              decoding="async"
              data-testid={i === 0 ? `product-card-photo-${product.slug}` : `product-card-photo-${product.slug}-${i}`}
              draggable={false}
            />
          ))
        ) : (
          <ProductVisual
            name={product.name}
            format={product.format}
            badgeTestId={`format-badge-${product.slug}`}
          />
        )}
        {product.coming_soon && (
          <span className="absolute left-3 top-3 z-10 rounded-full bg-amber-500 px-2.5 py-1 font-mono text-[10px] font-bold tracking-[0.12em] text-white" data-testid={`coming-soon-badge-${product.slug}`}>
            COMING SOON
          </span>
        )}
        {!product.coming_soon && oos && (
          <span className="absolute left-3 top-3 z-10 rounded-full bg-red-600 px-2.5 py-1 font-mono text-[10px] font-bold tracking-[0.12em] text-white" data-testid={`oos-badge-${product.slug}`}>
            OUT OF STOCK
          </span>
        )}
        {!product.coming_soon && !oos && (product.is_best_seller || product.is_new_batch || product.is_bogo) && (
          // Stacked badges, fixed order so cards look designed, not random:
          //   1. BEST SELLER (top)   2. NEW BATCH   3. BUY 1 GET 1 FREE
          // Badges are manual per-product flags, nothing is auto-calculated,
          // nothing is on by default. See seed_data.py + product_overrides.
          <div className="absolute left-3 top-3 z-10 flex flex-col gap-1.5" data-testid={`badges-${product.slug}`}>
            {product.is_best_seller && (
              <span className="rounded-full bg-brandblue px-2.5 py-1 font-mono text-[10px] font-bold tracking-[0.12em] text-white" data-testid={`badge-best-seller-${product.slug}`}>
                BEST SELLER
              </span>
            )}
            {product.is_new_batch && (
              <span className="rounded-full bg-branddeep px-2.5 py-1 font-mono text-[10px] font-bold tracking-[0.12em] text-white" data-testid={`badge-new-batch-${product.slug}`}>
                NEW BATCH
              </span>
            )}
            {product.is_bogo && (
              <span className="rounded-full bg-navy px-2.5 py-1 font-mono text-[10px] font-bold tracking-[0.12em] text-white" data-testid={`badge-bogo-${product.slug}`}>
                BUY 1 GET 1 FREE
              </span>
            )}
          </div>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center justify-between">
          <p className="label-mono">{product.categories?.[0]}</p>
          <Star className="h-3.5 w-3.5 fill-brandblue text-brandblue" />
        </div>
        <Link to={`/product/${product.slug}`}>
          <h3 className="mt-1 font-heading text-lg font-bold leading-tight text-ink hover:text-brandblue transition-colors" data-testid={`product-card-title-${product.slug}`}>
            {main}
          </h3>
          {sub && (
            <p className="mt-1 font-mono text-[11px] leading-snug text-subtle" data-testid={`product-card-subtitle-${product.slug}`}>
              {sub}
            </p>
          )}
        </Link>
        <p className="mt-1.5 line-clamp-2 text-sm text-subtle">{getCardShortDescription(product)}</p>
        {!product.coming_soon && (
          // Micro-label system (Round 2): IN STOCK · BEST SELLER · COA VERIFIED
          // Always renders COA VERIFIED. Stock chip switches between OUT OF
          // STOCK, ONLY N LEFT and IN STOCK based on live admin data so
          // buyers see the same signal the admin edited moments ago.
          // BEST SELLER only when the admin flag is set (avoids visual clutter).
          <ul className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] tracking-[0.14em] text-subtle" data-testid={`micro-labels-${product.slug}`}>
            {oos ? (
              <li className="flex items-center gap-1 text-red-600" data-testid={`stock-chip-${product.slug}`}>
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden="true" /> OUT OF STOCK
              </li>
            ) : cardStockChip ? (
              <li
                className={`flex items-center gap-1 ${
                  cardStockChip.tone === "low" ? "text-amber-700" : "text-emerald-700"
                }`}
                data-testid={`stock-chip-${product.slug}`}
              >
                <span
                  className={`inline-block h-1.5 w-1.5 rounded-full ${
                    cardStockChip.tone === "low" ? "bg-amber-500" : "bg-emerald-500"
                  }`}
                  aria-hidden="true"
                />{" "}
                {cardStockChip.text}
              </li>
            ) : (
              <li className="flex items-center gap-1 text-emerald-700" data-testid={`stock-chip-${product.slug}`}>
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" /> IN STOCK
              </li>
            )}
            {product.is_best_seller && (
              <li className="flex items-center gap-1 text-brandblue">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-brandblue" aria-hidden="true" /> BEST SELLER
              </li>
            )}
            <li className="flex items-center gap-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brandbluedark" aria-hidden="true" /> COA VERIFIED
            </li>
          </ul>
        )}
        {product.format === "raw" && (
          <p className="mt-2 inline-flex w-fit items-center gap-1.5 rounded-sm bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700" data-testid={`raw-warning-${product.slug}`}>
            <AlertTriangle className="h-3 w-3" strokeWidth={2} /> Reconstitution equipment required
          </p>
        )}
        <div className="mt-auto pt-5">
          {product.coming_soon ? (
            <NotifyForm productId={product.id} slug={product.slug} context="restock-coming-soon" />
          ) : oos ? (
            // OOS reuses the same NotifyForm/notify-requests pipeline the
            // Coming-Soon flow uses, no separate "back-in-stock" system.
            <NotifyForm productId={product.id} slug={product.slug} context="restock" />
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-2">
                {isTrueMultiVariant && (
                  <span className="font-mono text-[10px] tracking-[0.14em] text-subtle" data-testid={`from-label-${product.slug}`}>FROM</span>
                )}
                <span className="font-heading text-xl font-black text-ink">{fmtPrice(product.price)}</span>
                {product.original_price && (
                  <span className="text-sm text-subtle line-through">{fmtPrice(product.original_price)}</span>
                )}
              </div>
              <button
                onClick={() => {
                  if (isTrueMultiVariant) {
                    window.location.assign(`/product/${product.slug}`);
                    return;
                  }
                  // Single-SKU: if the product has exactly one variant we
                  // MUST pass it through so the cart/order carry the
                  // required variant_pack_size and correct variant price.
                  if (variantList.length === 1) {
                    addItem(product, 1, variantList[0]);
                  } else {
                    addItem(product);
                  }
                }}
                className="btn-pill bg-navy px-5 py-2 text-xs font-bold tracking-wide text-white hover:bg-navydark"
                data-testid={`add-to-cart-${product.slug}`}
              >
                {isTrueMultiVariant ? "CHOOSE" : "ADD"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
